import { API_CONFIG, STORAGE_KEYS, CACHE_CONFIG } from './constants';

class APIConfig {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.retryAttempts = API_CONFIG.RETRY_ATTEMPTS;
    this.retryDelay = API_CONFIG.RETRY_DELAY;
    this.cache = new Map();
    this.requestQueue = new Map();
  }

  getAuthToken() {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }
    return null;
  }

  getRefreshToken() {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    }
    return null;
  }

  setAuthToken(token) {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
    }
  }

  setRefreshToken(token) {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
    }
  }

  clearTokens() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }
  }

  getUserData() {
    if (typeof window !== 'undefined') {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    }
    return null;
  }

  setUserData(userData) {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    }
  }

  clearUserData() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    }
  }

  getCacheKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    return `${url}_${JSON.stringify(sortedParams)}`;
  }

  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key);
    }
    return null;
  }

  setCache(key, data, ttl = CACHE_CONFIG.DEFAULT_TTL) {
    this.cache.set(key, {
      data,
      expiresAt: Date.now() + ttl
    });
  }

  clearCache() {
    this.cache.clear();
  }

  clearExpiredCache() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (value.expiresAt <= now) {
        this.cache.delete(key);
      }
    }
  }

  getHeaders(customHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders
    };

    const token = this.getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  buildURL(endpoint, params = {}) {
    const fullUrl = `${this.baseURL}${endpoint}`;
    const url = new URL(fullUrl);

    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        url.searchParams.append(key, params[key]);
      }
    });

    return url.toString();
  }

  isRequestInQueue(key) {
    return this.requestQueue.has(key);
  }

  addToQueue(key, promise) {
    this.requestQueue.set(key, promise);
  }

  removeFromQueue(key) {
    this.requestQueue.delete(key);
  }

  getFromQueue(key) {
    return this.requestQueue.get(key);
  }

  clearQueue() {
    this.requestQueue.clear();
  }

  getEnvironment() {
    return process.env.NODE_ENV || 'development';
  }

  isDevelopment() {
    return this.getEnvironment() === 'development';
  }

  isProduction() {
    return this.getEnvironment() === 'production';
  }

  getAPIVersion() {
    return 'v1';
  }

  getFullAPIURL(endpoint) {
    return `${this.baseURL}${endpoint}`;
  }

  isValidEndpoint(endpoint) {
    return endpoint && typeof endpoint === 'string' && endpoint.startsWith('/');
  }

  getDefaultTimeout() {
    return this.timeout;
  }

  getRetryConfig() {
    return {
      attempts: this.retryAttempts,
      delay: this.retryDelay
    };
  }
}

const apiConfig = new APIConfig();

export default apiConfig;
